import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const reistijdenKey = (seizoenId: string) =>
  ["beheer", "reistijden", seizoenId] as const;

/**
 * The OpenAPI document allows a numeric value on the wire as a string, so the generated client
 * types it as a union. Narrowed once here rather than at every use.
 */
export type Reistijd = Omit<components["schemas"]["ReistijdDto"], "seconds"> & {
  seconds: number | null;
};

/**
 * Every `double` is typed `number | string` by the generated client. Nothing else here carries a
 * float, so the union is narrowed at this boundary rather than spread through the components.
 */
export type GeocodeTreffer = Omit<
  components["schemas"]["GeocodeTreffer"],
  "latitude" | "longitude"
> & { latitude: number; longitude: number };

/** Narrows one wire value. `Number` rather than `parseFloat`: it rejects "12abc" instead of reading 12. */
export function alsGetal(waarde: number | string) {
  return typeof waarde === "number" ? waarde : Number(waarde);
}

function problem(error: unknown, response: Response, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? `${fallback} (${response.status}).`);
}

/**
 * The shortest query PDOK will accept, and so the shortest one worth sending. The Location API
 * requires `q` to be 2-200 characters and refuses anything shorter outright.
 */
export const GEOCODE_MINIMUM = 2;

/**
 * Pass an already-debounced string: every distinct value is a request to a third party
 * (docs/adr/0060-a-meldadres-is-chosen-not-typed.md). `keepPreviousData`, because a list that
 * empties between keystrokes is a list nobody can aim at.
 */
export function useGeocodeSuggesties(q: string) {
  const gezocht = q.trim();

  return useQuery({
    queryKey: ["beheer", "geocode", gezocht] as const,
    queryFn:
      gezocht.length < GEOCODE_MINIMUM
        ? skipToken
        : async () => {
            const { data, error, response } = await api.GET("/api/beheer/geocode", {
              params: { query: { q: gezocht } },
            });
            if (!data)
              throw problem(error, response, i18n.t("reistijden:errors.zoeken"));

            return data.map<GeocodeTreffer>((treffer) => ({
              ...treffer,
              latitude: alsGetal(treffer.latitude),
              longitude: alsGetal(treffer.longitude),
            }));
          },

    // What an address resolves to does not move while a form is open, and a beheerder
    // backspacing through a huisnummer walks back over strings they just asked for.
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * @param wachtOnberekend How many pairs were uncomputed when a fill was asked for; the query
 * polls until that count moves. Nothing exposes the job queue, so this is the only honest signal.
 */
export function useReistijden(
  seizoenId: string | undefined,
  wachtOnberekend?: number | null,
) {
  return useQuery({
    queryKey: reistijdenKey(seizoenId ?? ""),
    refetchInterval: (query) =>
      wachtOnberekend != null &&
      onberekend(query.state.data) === wachtOnberekend
        ? 2000
        : false,
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/seizoenen/{seizoenId}/reistijden",
            { params: { path: { seizoenId } } },
          );
          if (!data)
            throw problem(error, response, i18n.t("reistijden:errors.ophalen"));

          return data.map<Reistijd>((rij) => ({
            ...rij,
            seconds: rij.seconds == null ? null : alsGetal(rij.seconds),
          }));
        },
  });
}

export const korpsenZonderCoordinatenKey = () =>
  ["beheer", "korpsen-zonder-coordinaten"] as const;

/**
 * Every korps Portaal has no coordinates for, regardless of seizoen or wedstrijddag - what the
 * Reistijden screen warns on as soon as it opens, before a beheerder has chosen a wedstrijddag.
 */
export function useKorpsenZonderCoordinaten() {
  return useQuery({
    queryKey: korpsenZonderCoordinatenKey(),
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/beheer/korpsen/zonder-coordinaten",
        {},
      );
      if (!data)
        throw problem(error, response, i18n.t("reistijden:errors.ophalen"));

      return data;
    },
  });
}

export type OndersteunerReistijd = Omit<
  components["schemas"]["OndersteunerReistijdDto"],
  "seconds"
> & { seconds: number | null };

export const ondersteunersZonderPuntKey = () =>
  ["beheer", "ondersteuners-zonder-punt"] as const;

/**
 * Global like its korps twin: a postcode is a property of the persoon, not of a wedstrijddag. The
 * planning is not blocked on these - it ranks them on functierotatie alone (ADR-0053).
 */
export function useOndersteunersZonderPunt() {
  return useQuery({
    queryKey: ondersteunersZonderPuntKey(),
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/beheer/ondersteuners/zonder-punt",
        {},
      );
      if (!data)
        throw problem(error, response, i18n.t("reistijden:errors.ophalen"));

      return data;
    },
  });
}

export const ondersteunerReistijdenVoorDagKey = (
  wedstrijddagId: string,
  page: number | undefined,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  punt: string[],
) =>
  [
    "beheer",
    "ondersteuner-reistijden-voor-dag",
    wedstrijddagId,
    page,
    search,
    sortBy,
    sortDescending,
    punt,
  ] as const;

/** One wedstrijddag's row of the matrix against every ondersteuner - the persoon-axis twin of
 * {@link useReistijdenVoorDag}, a page at a time, searched and sorted on the server. */
export function useOndersteunerReistijdenVoorDag(
  wedstrijddagId: string | undefined,
  page: number | undefined,
  pageSize: number,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  /** One of `OndersteunerPunt`'s names, matching {@link OndersteunerReistijd.punt} on the wire. */
  punt?: string[],
) {
  return useQuery({
    queryKey: ondersteunerReistijdenVoorDagKey(
      wedstrijddagId ?? "",
      page,
      search,
      sortBy,
      sortDescending,
      punt ?? [],
    ),
    queryFn: !wedstrijddagId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/wedstrijddagen/{wedstrijddagId}/ondersteuner-reistijden",
            {
              params: {
                path: { wedstrijddagId },
                query: {
                  page,
                  pageSize,
                  search: search || undefined,
                  sortBy,
                  sortDescending,
                  // Left off entirely when nothing is ticked, matching PersoonZoeker's functie
                  // filter: an unfiltered request stays the same request it always was.
                  ...(punt?.length ? { punt } : {}),
                },
              },
            },
          );
          if (!data)
            throw problem(error, response, i18n.t("reistijden:errors.ophalen"));

          return {
            ...data,
            page: {
              ...data.page,
              items: data.page.items.map<OndersteunerReistijd>((rij) => ({
                ...rij,
                seconds: rij.seconds == null ? null : alsGetal(rij.seconds),
              })),
            },
          };
        },
    // Same reason as the korps table: a page never fetched before would otherwise unmount the
    // pager button that was just clicked.
    placeholderData: keepPreviousData,
  });
}

export const reistijdenVoorDagKey = (
  wedstrijddagId: string,
  page: number | undefined,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  bron: string[],
) =>
  [
    "beheer",
    "reistijden-voor-dag",
    wedstrijddagId,
    page,
    search,
    sortBy,
    sortDescending,
    bron,
  ] as const;

/**
 * A page at a time, searched and sorted on the server. The three counts describe the whole dag
 * regardless of which page is on screen (see `ReistijdenVoorDagResult`).
 */
export function useReistijdenVoorDag(
  wedstrijddagId: string | undefined,
  page: number | undefined,
  pageSize: number,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  /** "handmatig", "berekend" or "geen" - the table's own derived bron column, not the wire `bron`. */
  bron?: string[],
) {
  return useQuery({
    queryKey: reistijdenVoorDagKey(
      wedstrijddagId ?? "",
      page,
      search,
      sortBy,
      sortDescending,
      bron ?? [],
    ),
    queryFn: !wedstrijddagId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/wedstrijddagen/{wedstrijddagId}/reistijden",
            {
              params: {
                path: { wedstrijddagId },
                query: {
                  page,
                  pageSize,
                  search: search || undefined,
                  sortBy,
                  sortDescending,
                  ...(bron?.length ? { bron } : {}),
                },
              },
            },
          );
          if (!data)
            throw problem(error, response, i18n.t("reistijden:errors.ophalen"));

          return {
            ...data,
            page: {
              ...data.page,
              items: data.page.items.map<Reistijd>((rij) => ({
                ...rij,
                seconds: rij.seconds == null ? null : alsGetal(rij.seconds),
              })),
            },
          };
        },
    // Each page is its own query key, so without this a click to a page never fetched before
    // clears `data` and swaps the table for a skeleton - unmounting the very pager button just
    // clicked, which loses focus and (in some browsers) scrolls the page back to the top. The
    // previous page's rows stay on screen, correct but stale, until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}

/**
 * The pairs this seizoen has no answer for yet. `null` seconds is what uncomputed means
 * (ADR-0013): a pair with no row is still listed, so the korps is visible rather than omitted.
 */
export function onberekend(rijen: Reistijd[] | undefined) {
  return rijen?.filter((rij) => rij.seconds == null).length ?? 0;
}

/**
 * Asks for every reistijd of the seizoen to be computed again, hand-set rows excepted. There is
 * nothing to check first - a beheerder pressing the Reistijden screen's Berekenen button means it
 * regardless of whether anything currently looks cold.
 */
export function useHerberekenReistijden(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST(
        "/api/beheer/seizoenen/{seizoenId}/reistijden/herbereken",
        { params: { path: { seizoenId } } },
      );
      if (!data)
        throw problem(error, response, i18n.t("reistijden:errors.berekenen"));

      return data.map<Reistijd>((rij) => ({
        ...rij,
        seconds: rij.seconds == null ? null : alsGetal(rij.seconds),
      }));
    },
    onSuccess: (rijen) =>
      queryClient.setQueryData(reistijdenKey(seizoenId), rijen),
  });
}

export function useSetReistijd(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: {
      korpsId: string;
      wedstrijddagId: string;
      minutes: number;
    }) => {
      const { error, response } = await api.PUT("/api/beheer/reistijden", {
        body,
      });
      if (!response.ok)
        throw problem(error, response, i18n.t("reistijden:errors.opslaan"));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: reistijdenKey(seizoenId) }),
  });
}

export function useClearReistijd(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (query: { korpsId: string; wedstrijddagId: string }) => {
      const { error, response } = await api.DELETE("/api/beheer/reistijden", {
        params: { query },
      });
      if (!response.ok)
        throw problem(error, response, i18n.t("reistijden:errors.terugzetten"));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: reistijdenKey(seizoenId) }),
  });
}

/**
 * Minutes, rounded. The number is a sort key and is never shown to a korps - this screen is the
 * one place a human reads it, and reading it to the second helps nobody.
 */
export function alsMinuten(seconds: number) {
  return Math.round(seconds / 60);
}

/**
 * `45 min` under the hour, `2:37` at or above it: past sixty, minutes stop being a quantity
 * anyone can picture, and this table is scanned rather than read.
 */
export function alsReistijd(seconds: number) {
  const minuten = alsMinuten(seconds);

  if (minuten < 60) {
    return `${minuten} min`;
  }

  return `${Math.floor(minuten / 60)}:${String(minuten % 60).padStart(2, "0")}`;
}
